import { useLazyQuery, useMutation } from '@apollo/client';
import { UseFormReturn } from 'react-hook-form';
import { useRouter } from 'next/compat/router';
import { createPromptMutation } from './mutations/createPromptMutation';
import { TPromptFormFields } from '../PromptForm/types';
import { getPromptByIdQuery } from './queries/getPromptByIdQuery';
import { getPromptPageUrl } from '../../services/pagesUrl/getPromptPageUrl';
import { PromptForm } from '../PromptForm/PromptForm';
import { getSortedPromptsQuery } from '../Prompt/queries/getSortedPromptsQuery';
import { useAnalytics } from '../AnalyticsProvider/AnalyticsProvider';
import { showPromptErrorNotification } from './helpers/showPromptErrorNotification';
import { PromptSettingsLiveStatus, PromptTypePicker } from '../Prompt/constants';

type TCreatePromptFormProps = {
  territoryId: string;
  closeModal: () => void;
  useFormReturn: UseFormReturn<TPromptFormFields>;
  promptSettingsLiveStatus?: PromptSettingsLiveStatus;
  promptType?: PromptTypePicker;
  setPromptType?: (type: PromptTypePicker) => void;
};

export const CreatePromptForm = ({
  territoryId,
  closeModal,
  useFormReturn,
  promptSettingsLiveStatus,
  promptType,
  setPromptType,
}: TCreatePromptFormProps) => {
  const { sendEvent } = useAnalytics();
  const router = useRouter();
  const [createPromptRequest] = useMutation(createPromptMutation, {
    refetchQueries: [getSortedPromptsQuery],
  });

  const [getPromptByIdRequest] = useLazyQuery(getPromptByIdQuery);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const onSubmit = async ({ title, description, startDate, endDate }: TPromptFormFields) => {
    let promptSlug;
    try {
      sendEvent('territory_create_prompt_button_click', { prompt_type: promptType as PromptTypePicker });
      sendEvent('create_prompt_initiated');
      const createPromptResponse = await createPromptRequest({
        variables: {
          title,
          description,
          territoryId,
          startDate: promptType === PromptTypePicker.LIVE ? startDate : undefined,
          endDate: promptType === PromptTypePicker.LIVE ? endDate : undefined,
        },
      });
      const promptId = createPromptResponse.data.createPrompt.article.id;
      const getPromptResponse = await getPromptByIdRequest({ variables: { id: promptId } });
      const prompt = getPromptResponse.data.article;
      promptSlug = prompt?.slug;

      sendEvent('create_prompt_success', { prompt_id: promptId });

      router?.push(getPromptPageUrl(promptSlug));
      closeModal();
    } catch (error) {
      sendEvent('create_prompt_error');

      showPromptErrorNotification();
    }
  };
  return (
    <PromptForm
      onSubmit={onSubmit}
      submitButtonLabel="Create prompt"
      useFormReturn={useFormReturn}
      promptSettingsLiveStatus={promptSettingsLiveStatus}
      setPromptType={setPromptType}
    />
  );
};
